import React, { useState } from 'react';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';

function ZReportList({ 
  reports, 
  pagination,
  loading,
  onDownload, 
  onDelete, 
  onView, 
  onFilterChange, 
  onPageChange,
  onPerPageChange,
  filters,
  renderLayout,
}) {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePerPageChange = (perPage) => {
    if (onPerPageChange) {
      onPerPageChange(perPage);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { class: 'badge bg-warning', text: 'Borrador' },
      'generated': { class: 'badge bg-success', text: 'Generado' },
    };
    
    const config = statusConfig[status] || { class: 'badge bg-primary', text: status };
    return <span className={config.class}>{config.text}</span>;
  };

  const generatePageNumbers = () => {
    if (!pagination) return [];
    
    const pages = [];
    const maxVisiblePages = 5;
    const currentPage = pagination.current_page;
    const lastPage = pagination.last_page;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(lastPage, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handlePageChange = (page) => {
    if (onPageChange && page >= 1 && page <= pagination?.last_page) {
      onPageChange(page);
    }
  };

  const nextPage = () => {
    if (pagination?.has_more_pages) {
      handlePageChange(pagination.current_page + 1);
    }
  };

  const prevPage = () => {
    if (pagination && pagination.current_page > 1) {
      handlePageChange(pagination.current_page - 1);
    }
  };

  const toolbar = (
    <div className="row">
      <div className="col-md-3 mb-2">
        <label className="form-label">Fecha desde</label>
        <input
          type="date"
          className="form-control"
          value={localFilters.start_date}
          onChange={(e) => handleFilterChange('start_date', e.target.value)}
        />
      </div>
      <div className="col-md-3 mb-2">
        <label className="form-label">Fecha hasta</label>
        <input
          type="date"
          className="form-control"
          value={localFilters.end_date}
          onChange={(e) => handleFilterChange('end_date', e.target.value)}
        />
      </div>
      <div className="col-md-6 mb-2 d-flex align-items-end">
        <button
          className="btn btn-outline-secondary me-2 zreport-btn-outline-secondary"
          onClick={() => {
            setLocalFilters({ start_date: '', end_date: '' });
            onFilterChange({ start_date: '', end_date: '' });
          }}
        >
          <i className="bi bi-x-circle me-1"></i>
          Limpiar filtros
        </button>
      </div>
    </div>
  );

  const emptyState = (
    <div className="text-center py-5">
      <i className="bi bi-receipt display-1 text-muted"></i>
      <h5 className="mt-3 text-muted">No hay reportes Z</h5>
      <p className="text-muted">Genera tu primer reporte Z para comenzar</p>
    </div>
  );

  const tableContent = loading ? (
    <div className="text-center py-4">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
    </div>
  ) : reports.length === 0 ? (
    emptyState
  ) : (
    <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th className='text-center'>ID</th>
                  <th>Período</th>
                  <th>Origen</th>
                  <th className='text-end'>Solicitudes</th>
                  <th className='text-end'>Cobros</th>
                  <th className='text-end'>Anulaciones</th>
                  <th className='text-end'>Total</th>
                  <th className='text-center'>Estado</th>
                  <th className='text-center'>Fecha Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td className='text-center'>
                      <strong>#{report.id}</strong>
                    </td>
                    <td>
                      <div>
                        <small className="text-muted">Desde:</small><br />
                        {formatDateTime(report.period.from)}
                      </div>
                      <div className="mt-1">
                        <small className="text-muted">Hasta:</small><br />
                        {formatDateTime(report.period.to)}
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{report.user.name}</strong>
                      </div>
                    </td>
                    <td className='text-end'>
                      <div className="fw-bold text-success">
                        {formatCurrency(report.totals.sold_amount)}
                      </div>
                      <small className="text-muted">
                        {report.totals.requests_count} solicitudes
                      </small>
                    </td>
                    <td className='text-end'>
                      <div className="fw-bold text-primary">
                        {formatCurrency(report.totals.collected_amount)}
                      </div>
                      <small className="text-muted">
                        {report.totals.payments_count} cobros
                      </small>
                    </td>
                    <td className='text-end'>
                      <div className="fw-bold text-danger">
                        {formatCurrency(report.totals.annulments_amount)}
                      </div>
                      <small className="text-muted">
                        {report.totals.annulments_count} anulaciones
                      </small>
                    </td>
                    <td className='text-end'>
                      <div className="fw-bold text-dark">
                        {formatCurrency(report.totals.amount)}
                      </div>
                    </td>
                    <td className='text-center'>
                      {getStatusBadge(report.status)}
                    </td>
                    <td className='text-center'>
                      {formatDateTime(report.created_at)}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onView(report.id)}
                          title="Ver detalle"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => onDownload(report.id)}
                          title="Descargar PDF"
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDelete(report.id)}
                          title="Eliminar"
                          disabled={report.status !== 'generated'}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
  );

  const paginationContent = pagination && !loading && reports.length > 0 ? (
    <div className="d-flex justify-content-between align-items-center">
      <div className="zreport-per-page-selector">
        <label className="form-label mb-0 me-2">Mostrar:</label>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={pagination?.per_page || 10}
          onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-muted ms-2">por página</span>
      </div>
      <div className="d-flex align-items-center">
        <div className="zreport-pagination-info me-3">
          Mostrando {pagination.from} a {pagination.to} de {pagination.total} resultados
        </div>
        <nav aria-label="Navegación de páginas">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={prevPage}
                disabled={pagination.current_page === 1}
              >
                Anterior
              </button>
            </li>
            {generatePageNumbers().map((page) => (
              <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            <li className={`page-item ${!pagination.has_more_pages ? 'disabled' : ''}`}>
              <button 
                className="page-link"
                onClick={nextPage}
                disabled={!pagination.has_more_pages}
              >
                Siguiente
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  ) : null;

  if (renderLayout) {
    return renderLayout({ toolbar, table: tableContent, pagination: paginationContent });
  }

  return (
    <div>
      {toolbar}
      {tableContent}
      {paginationContent}
    </div>
  );
}

export default ZReportList; 